import { useContext, useEffect, useState, useRef } from 'react';

import { CallWithDuration } from '../types';
import { TwilioContext } from '../context-providers/TwilioContext';
import { updateCallHoldStatus, hangupCall, getParticipants } from '../api';
import { logService } from '../services';

type LoadingStates = {
  hold: boolean;
  mute: boolean;
  hangup: boolean;
  accept: boolean;
  decline: boolean;
};

// Extended call type with cached hold state
type CallWithHoldState = CallWithDuration & {
  _onHold?: boolean;
  _holdPending?: boolean;
};

const useCallControls = (call: CallWithDuration | undefined) => {
  const callWithState = call as CallWithHoldState | undefined;

  const [status, setStatus] = useState<string>();
  const [isMuted, setIsMuted] = useState<boolean>(false);
  // Initialize from cached state on call object if available
  const [isHolded, setIsHolded] = useState<boolean>(() => callWithState?._onHold ?? false);
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    hold: false,
    mute: false,
    hangup: false,
    accept: false,
    decline: false,
  });
  const [callDuration, setCallDuration] = useState(0);
  const [hangupCooldown, setHangupCooldown] = useState(false);
  // Initialize from cached state on call object if available
  const [holdPending, setHoldPending] = useState<boolean>(() => callWithState?._holdPending ?? false);
  const holdPollRef = useRef<NodeJS.Timeout | null>(null);
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { forwardCall } = useContext(TwilioContext);

  // Helper function to wrap async actions with loading state
  const withLoading = async <T, >(
    action: keyof LoadingStates,
    fn: () => Promise<T>,
  ): Promise<T | undefined> => {
    if (loadingStates[action]) return;

    setLoadingStates(prev => ({ ...prev, [action]: true }));
    try {
      return await fn();
    } finally {
      setLoadingStates(prev => ({ ...prev, [action]: false }));
    }
  };

  useEffect(() => {
    const durationInitial = call?.durationInitial;
    const durationFinal = call?.durationFinal;

    if (!call || !durationInitial) return;

    let intervalId: NodeJS.Timeout | null = null;

    // Call has ended if durationFinal is set OR status is 'closed'
    const isCallEnded = durationFinal || status === 'closed';

    if (isCallEnded) {
      // If call has ended, calculate total duration (use durationFinal if available, otherwise freeze at current time)
      const endTime = durationFinal || Date.now();
      setCallDuration(Math.floor((endTime - durationInitial) / 1000));
    } else {
      // If call is still ongoing, start a timer to count seconds
      setCallDuration(Math.floor((Date.now() - durationInitial) / 1000));
      intervalId = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - durationInitial) / 1000));
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [call, status]);

  useEffect(() => {
    if (!call) {
      return;
    }

    setStatus(call.status());

    // Sync hold state from call object cache (for when component mounts after state was set)
    const callWithState = call as CallWithHoldState;
    if (callWithState._onHold !== undefined) {
      setIsHolded(callWithState._onHold);
    }
    if (callWithState._holdPending !== undefined) {
      setHoldPending(callWithState._holdPending);
    }

    const onCallStatusChange = () => {
      setStatus(call.status());
    };

    const onMuteChange = (isMuted: boolean) => {
      setIsMuted(isMuted);
    };

    const onHoldChange = (isHolded: boolean) => {
      // Cache on call object for new hook instances to read
      (call as CallWithHoldState)._onHold = isHolded;
      setIsHolded(isHolded);
    };

    const onHoldPendingChange = (pending: boolean) => {
      // Cache on call object for new hook instances to read
      (call as CallWithHoldState)._holdPending = pending;
      setHoldPending(pending);
    };

    // https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#events
    call.on('accept', onCallStatusChange);
    call.on('cancel', onCallStatusChange);
    call.on('disconnect', onCallStatusChange);
    call.on('error', onCallStatusChange);
    call.on('reconnected', onCallStatusChange);
    call.on('reconnecting', onCallStatusChange);
    call.on('reject', onCallStatusChange);
    call.on('ringing', onCallStatusChange);
    call.on('mute', onMuteChange);
    call.on('hold', onHoldChange);
    call.on('holdPending', onHoldPendingChange);

    return () => {
      call.off('accept', onCallStatusChange);
      call.off('cancel', onCallStatusChange);
      call.off('disconnect', onCallStatusChange);
      call.off('error', onCallStatusChange);
      call.off('reconnected', onCallStatusChange);
      call.off('reconnecting', onCallStatusChange);
      call.off('reject', onCallStatusChange);
      call.off('ringing', onCallStatusChange);
      call.off('mute', onMuteChange);
      call.off('hold', onHoldChange);
      call.off('holdPending', onHoldPendingChange);
    };
  }, [call]);

  const getCustomerPhoneNumber = () => {
    if (!call) return undefined;
    if (call.customParameters.get('FromClient')) {
      return call.customParameters.get('FromClient');
    }

    return call.customParameters.get('To');
  };

  const clearHoldPolling = () => {
    if (holdPollRef.current) {
      clearInterval(holdPollRef.current);
      holdPollRef.current = null;
    }
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
  };

  const holdCall = async() => {
    if (!call) return;
    if (holdPending) return;

    const targetHold = !isHolded;
    call.emit('holdPending', true);

    try {
      const callSid = call.parameters.CallSid;
      await updateCallHoldStatus({ call_sid: callSid, hold: targetHold });

      // Poll until backend confirms (callback received)
      holdPollRef.current = setInterval(async() => {
        try {
          const response = await getParticipants(callSid);
          if (response.on_hold === targetHold) {
            call.emit('holdPending', false);
            call.emit('hold', targetHold);
            clearHoldPolling();
          }
        } catch (error) {
          console.error('Error polling hold status:', error);
        }
      }, 1000);

      // Timeout fallback after 10 seconds
      holdTimeoutRef.current = setTimeout(() => {
        if (holdPollRef.current) {
          clearHoldPolling();
          call.emit('holdPending', false);
          // Assume success after timeout and update UI
          call.emit('hold', targetHold);
          logService.addLog('Hold status timeout - assumed success');
        }
      }, 10000);
    } catch (error) {
      call.emit('holdPending', false);
      clearHoldPolling();
      const message = `Error while holding the call \n${error}`;
      console.error(message);
      logService.addLog(message);
    }
  };

  const accept = async() => {
    if (!call) return;
    await withLoading('accept', async() => {
      await forwardCall(call);
      logService.addLog('Incoming call accepted.');
    });
  };

  const decline = async() => {
    if (!call) return;
    await withLoading('decline', async() => {
      await call.reject();
      logService.addLog('Incoming call rejected.');
    });
  };

  const sendDigits = async(digits: string) => {
    if (!call) return;
    await call.sendDigits(digits);
    logService.addLog(`Sent digits: ${digits}.`);
  };

  const hangUp = async() => {
    if (!call) return;
    if (hangupCooldown) return;

    setHangupCooldown(true);
    setTimeout(() => {
      setHangupCooldown(false);
    }, 3000);

    await withLoading('hangup', async() => {
      try {
        const callSid = call.parameters.CallSid;

        call.disconnect();
        logService.addLog('Call disconnected locally');

        hangupCall({ call_sid: callSid }).catch((error) => {
          console.error('Backend hangup request failed:', error);
          logService.addLog(`Backend hangup error: ${error}`);
        });

        logService.addLog('Hangup initiated');
      } catch (error) {
        const message = `Error while hanging up the call \n${error}`;
        console.error(message);
        logService.addLog(message);
      }
    });
  };

  const toggleMute = async() => {
    if (!call) return;
    await withLoading('mute', async() => {
      await call.mute(!call.isMuted());
      logService.addLog('Call is muted');
    });
  };

  return {
    getCustomerPhoneNumber,
    accept,
    decline,
    sendDigits,
    hangUp,
    toggleMute,
    status,
    isMuted,
    isHolded,
    loadingStates,
    callDuration,
    holdCall,
    hangupCooldown,
    holdPending,
  };
};

export default useCallControls;
